<template>
	<article class="trip-cargo-card">
		<div class="trip-cargo-card__content">
			<div class="trip-cargo-card__header">
				<span class="trip-cargo-card__number">Груз #{{ cargo.number }}</span>
				<span
					v-if="cargo.deliveryToClient || cargo.deliveryFromTruck"
					class="trip-cargo-card__type"
					:class="{
						'trip-cargo-card__type--delivery': cargo.deliveryToClient,
						'trip-cargo-card__type--pickup': !cargo.deliveryToClient && cargo.deliveryFromTruck
					}"
				>
					<span class="trip-cargo-card__type-dot"></span>
					{{ cargo.deliveryToClient ? 'Доставка с фуры' : 'Выдача с фуры' }}
				</span>
			</div>

			<div class="trip-cargo-card__description">
				<div class="trip-cargo-card__description-title">Описание:</div>
				<div class="trip-cargo-card__description-value">{{ cargo.name }}</div>

				<div class="trip-cargo-card__metrics">
					<div class="trip-cargo-card__metric">
						<span class="trip-cargo-card__metric-label">Вес, т</span>
						<span class="trip-cargo-card__metric-value">{{ cargo.weight }}</span>
					</div>
					<div class="trip-cargo-card__metric">
						<span class="trip-cargo-card__metric-label">Объём, м³</span>
						<span class="trip-cargo-card__metric-value">{{ cargo.volume }}</span>
					</div>
					<div class="trip-cargo-card__metric">
						<span class="trip-cargo-card__metric-label">Кол-во</span>
						<span class="trip-cargo-card__metric-value">{{ cargo.qty }}</span>
					</div>
					<div
						v-if="cargo.dimensions"
						class="trip-cargo-card__metric"
					>
						<span class="trip-cargo-card__metric-label">Габариты</span>
						<span class="trip-cargo-card__metric-value">{{ cargo.dimensions }}</span>
					</div>
				</div>
			</div>

			<div
				v-if="cargo.addressIn"
				class="trip-cargo-card__address"
			>
				<strong>Адрес:</strong> {{ cargo.addressIn }}
			</div>

			<UiButton
				class="trip-cargo-card__open"
				:color="ButtonType.primary"
				:size="ButtonSizeType.medium"
				@click="$emit('open', cargo)"
			>
				Открыть
			</UiButton>
		</div>

		<div
			class="trip-cargo-card__status"
			:style="{ backgroundColor: cargo?.cargoStatus?.backgroundColor, color: cargo?.cargoStatus?.color }"
		>
			{{ cargo?.cargoStatus?.name || '' }}
		</div>
	</article>
</template>

<script setup lang="ts">
	import type { ICargoByTripItem } from '~~/shared/interfaces/truck';
	import { ButtonSizeType, ButtonType } from '~~/shared/interfaces/ui';

	defineProps<{
		cargo: ICargoByTripItem;
	}>();

	defineEmits<{
		open: [cargo: ICargoByTripItem];
	}>();
</script>

<style scoped lang="scss">
	.trip-cargo-card {
		display: flex;
		overflow: hidden;
		width: 100%;
		border-radius: 20px;

		&__content {
			display: flex;
			flex: 1;
			min-width: 0;
			flex-direction: column;
			padding: 10px 21px 14px;
			background-color: var(--white);
		}

		&__header {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: 8px;
			margin-bottom: 10px;
		}

		&__number {
			color: #e32f3e;
			font-size: 14px;
			font-weight: 700;
		}

		&__type {
			display: inline-flex;
			align-items: center;
			gap: 5px;
			border-radius: 14px;
			padding: 3px 4px;
			color: #18212a;
			font-size: 10px;
			line-height: 1;
			background-color: #d8f2ef;

			&--pickup {
				background-color: #dceaff;
			}
		}

		&__type-dot {
			width: 11px;
			height: 11px;
			flex: 0 0 11px;
			border-radius: 50%;
			background-color: #36b7ae;
		}

		&__type--pickup &__type-dot {
			background-color: #4180ff;
		}

		&__description {
			border-radius: 18px;
			padding: 14px 21px 20px;
			background-color: #e9ecf4;
		}

		&__description-title,
		&__description-value {
			color: #3c4442;
			font-size: 12px;
			line-height: 1;
		}

		&__description-title {
			font-weight: 700;
		}

		&__metrics {
			display: flex;
			flex-wrap: wrap;
			gap: 8px 12px;
			margin-top: 6px;
		}

		&__metric {
			display: flex;
			min-width: 66px;
			flex-direction: column;
			border-radius: 11px;
			padding: 7px 10px 6px;
			background-color: var(--white);
		}

		&__metric-label {
			color: #757484;
			font-size: 12px;
			line-height: 1;
			white-space: nowrap;
		}

		&__metric-value {
			margin-top: 3px;
			color: #303733;
			font-size: 12px;
			font-weight: 600;
			line-height: 1;
			white-space: nowrap;
		}

		&__address {
			margin-top: 8px;
			color: #303733;
			font-size: 12px;
			line-height: 1;

			strong {
				color: #171717;
				font-weight: 700;
			}
		}

		&__open {
			width: 100%;
			margin-top: 8px;
			cursor: pointer;
		}

		&__status {
			display: flex;
			width: 33px;
			flex: 0 0 33px;
			align-items: center;
			justify-content: center;
			color: var(--white);
			font-size: 14px;
			font-weight: 700;
			writing-mode: vertical-rl;
			transform: rotate(180deg);
			background-color: #e32f3e;

			&--delivered {
				background-color: #16b99d;
			}
		}
	}

	@media (max-width: 380px) {
		.trip-cargo-card {
			&__content {
				padding-right: 12px;
				padding-left: 12px;
			}

			&__description {
				padding-right: 12px;
				padding-left: 12px;
			}

			&__metrics {
				gap: 8px;
			}

			&__metric {
				padding-right: 8px;
				padding-left: 8px;
			}
		}
	}
</style>
